import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSalesforceV1Service, serviceName } from '../service';
import { SalesforceAccountBySlugIO, ServiceQueries } from '../types';
import { useSalesforceV1Queries } from './use-salesforce-v1-queries';
import { UseSalesforceV1QueryEndpointArgs, useSalesforceV1Query } from './use-salesforce-v1-query';

/**
 * A hook that returns a query for the `SalesforceAccountBySlug` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useSalesforceAccountBySlugQuery = <E = unknown, D = SalesforceAccountBySlugIO['output']>(
  args: UseSalesforceV1QueryEndpointArgs<'SalesforceAccountBySlug', E, D>
) =>
  useSalesforceV1Query({
    endpointName: 'SalesforceAccountBySlug',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `SalesforceAccountBySlug` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useSalesforceAccountBySlugQueries = <E = unknown, D = SalesforceAccountBySlugIO['output']>(
  argsArr: UseSalesforceV1QueryEndpointArgs<'SalesforceAccountBySlug', E, D>[]
) =>
  useSalesforceV1Queries(
    argsArr.map((args) => ({
      endpointName: 'SalesforceAccountBySlug',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `SalesforceAccountBySlug` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchSalesforceAccountBySlug = () => {
  const queryClient = useQueryClient();

  const fetchSalesforceAccountBySlug = async <E = unknown, D = SalesforceAccountBySlugIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSalesforceV1QueryEndpointArgs<'SalesforceAccountBySlug', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      SalesforceAccountBySlugIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'SalesforceAccountBySlug'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        SalesforceAccountBySlugIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'SalesforceAccountBySlug'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'SalesforceAccountBySlug'>({
          serviceName,
          endpointName: 'SalesforceAccountBySlug',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSalesforceV1Service.SalesforceAccountBySlug(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchSalesforceAccountBySlug;
};
